/**
 * Which X this console writes to, named for a person about to write.
 *
 * The worker's smoke check answers `x.target`, the host its calls actually reach
 * (docs/API-CONTRACT.md, GET /api/v1/smoke). Two hosts are known: `X_ADS_SANDBOX_HOST`
 * and `X_ADS_HOST` in apps/worker/src/platform/x-api.ts. Only those two get a label;
 * any other host is shown as itself, with no guess at what it is. `x-host.test.ts`
 * reads both files as text, so the pair here cannot drift from them.
 *
 * The same file lives in ttads as `tiktok-host.ts`. Between 2026-09-15 and 2026-09-18 it
 * deliberately differed (e1, rule 22): TikTok documents its sandbox host, X's was only
 * configured, so this one said "Sandbox (unproven)" and warned on every host. The probe
 * of 2026-09-18 settled it, and the two files now say the same thing — for different
 * reasons, which is worth knowing: ttads's label rests on TikTok's documentation, this
 * one on a test account X created for us.
 */

/**
 * The host the worker uses for X_SANDBOX=1, **proven to be X's sandbox on 2026-09-18**:
 * `POST /12/accounts` there created the test account `gqb7mg`, which production cannot
 * see and which X named itself (docs/START-HERE.md, "Песочница доказана").
 *
 * 🔴 Proven for this host on that date, not for the name. X documents the sandbox at
 * `ads-api-sandbox.x.com`, which does not resolve; if X moves the sandbox there or
 * anywhere else, this label is a claim nobody has checked — put "(unproven)" and the
 * warnings back and run the probe again from step 1.
 */
export const SANDBOX_HOST = "ads-api-sandbox.twitter.com";
export const PRODUCTION_HOST = "ads-api.x.com";

/** What the badge says while the smoke check has not answered, or failed. */
export const UNKNOWN_HOST = "X host unknown";

export type XHost =
	| { kind: "sandbox" | "production" | "other"; host: string; text: string }
	| { kind: "unknown"; host: null; text: string };

/** Exact host names only: a lookalike such as `ads-api.x.com.example` is "other". */
export function xHost(target: string | null | undefined): XHost {
	if (!target) return { kind: "unknown", host: null, text: UNKNOWN_HOST };
	if (target === SANDBOX_HOST)
		return { kind: "sandbox", host: target, text: `Sandbox · ${target}` };
	if (target === PRODUCTION_HOST) {
		return { kind: "production", host: target, text: `Production · ${target}` };
	}
	return { kind: "other", host: target, text: target };
}
